import React, { FC } from 'react';

import clsx from 'clsx';

import { setPreQualificationStep, setSignInStep } from 'actions';
import { ArrowLeftImage, ArrowRightImage, Plus } from 'assets/images';
import { ButtonType } from 'enums/ButtonType';
import { PreQualificationStep } from 'enums/PreQualificationStep';
import { SignInStep } from 'enums/SignInStep';
import { useAppDispatch } from 'hooks/useAppDispatch';
import useAppNavigate from 'hooks/useAppNavigate';
import { AppRoutes } from 'routes/routes.enums';

import styles from './Button.module.scss';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  className?: string;
  isLoading?: boolean;
  disabled?: boolean;
  buttonType?: ButtonType;
  withArrow?: boolean;
  withPlus?: boolean;
  link?: string;
  applicationStep?: PreQualificationStep | SignInStep;
}

const getButtonStyles = (buttonType: ButtonType) => {
  switch (buttonType) {
    case ButtonType.Primary:
      return styles.primary;

    case ButtonType.Secondary:
      return styles.secondary;

    case ButtonType.TextButton:
      return styles.textButton;

    case ButtonType.Loading:
      return styles.loadingButton;

    default:
      return null as never;
  }
};

const renderButtonArrow = (children: React.ReactNode, buttonType: ButtonType) => {
  return (
    <>
      {buttonType === ButtonType.Secondary && <ArrowLeftImage className={styles.arrowLeft} />}
      {children}
      {buttonType === ButtonType.Primary && <ArrowRightImage className={styles.arrowRight} />}
    </>
  );
};
const renderButtonPlus = (children: React.ReactNode) => {
  return (
    <>
      <Plus className={styles.arrowLeft} />
      {children}
    </>
  );
};

const Button: FC<ButtonProps> = ({
  children,
  onClick,
  className,
  disabled = false,
  buttonType = ButtonType.Primary,
  withArrow = false,
  withPlus = false,
  link,
  applicationStep,
  isLoading = false,
  ...restProps
}) => {
  const navigator = useAppNavigate();
  const dispatch = useAppDispatch();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (onClick) {
      onClick(event);
    }

    if (!applicationStep) {
      return null;
    }

    if (applicationStep in PreQualificationStep) {
      dispatch(setPreQualificationStep(applicationStep as PreQualificationStep));
    }

    if (applicationStep in SignInStep) {
      dispatch(setSignInStep(applicationStep as SignInStep));
    }

    if (link) {
      navigator(link as AppRoutes);
    }
  };

  const renderButtonContent = () => {
    if (isLoading) {
      return (
        <div className={styles.loaderContainer}>
          <div className={styles.dotsElastic}></div>
        </div>
      );
    }

    if (withArrow) {
      return renderButtonArrow(children, buttonType);
    }

    if (withPlus) {
      return renderButtonPlus(children);
    }

    return children;
  };

  return (
    <button
      type="button"
      className={clsx(styles.button, getButtonStyles(isLoading ? ButtonType.Loading : buttonType), className)}
      onClick={handleClick}
      disabled={disabled || isLoading}
      {...restProps}
    >
      {renderButtonContent()}
    </button>
  );
};

export default Button;
